import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { habitacionApi } from '../api/habitacionApi';

export const useHabitaciones = () => {
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener todas las habitaciones
  const fetchHabitaciones = useCallback(async (moduloId = null, estadoHabitacionId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.getHabitaciones(moduloId, estadoHabitacionId);
      setHabitaciones(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener habitaciones';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener habitaciones con estadísticas
  const fetchHabitacionesWithStats = useCallback(async (moduloId = null, estadoHabitacionId = null, piso = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.getHabitacionesWithStats(moduloId, estadoHabitacionId, piso);
      setHabitaciones(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener habitaciones con estadísticas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener habitación por ID
  const fetchHabitacionById = useCallback(async (habitacionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.getHabitacionById(habitacionId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener habitación';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener habitaciones por módulo
  const fetchHabitacionesByModulo = useCallback(async (moduloId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.getHabitacionesByModulo(moduloId);
      setHabitaciones(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener habitaciones del módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear habitación
  const createHabitacion = useCallback(async (habitacionData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.createHabitacion(habitacionData);
      setHabitaciones(prev => [...prev, response.data]);
      toast.success('Habitación creada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al crear habitación';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar habitación
  const updateHabitacion = useCallback(async (habitacionId, habitacionData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.updateHabitacion(habitacionId, habitacionData);
      setHabitaciones(prev => 
        prev.map(habitacion => 
          habitacion.id === habitacionId ? response.data : habitacion
        )
      );
      toast.success('Habitación actualizada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al actualizar habitación';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar habitación
  const deleteHabitacion = useCallback(async (habitacionId) => {
    setLoading(true);
    setError(null);
    
    try {
      await habitacionApi.deleteHabitacion(habitacionId);
      setHabitaciones(prev => prev.filter(habitacion => habitacion.id !== habitacionId));
      toast.success('Habitación eliminada exitosamente');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al eliminar habitación';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar habitaciones
  const searchHabitaciones = useCallback(async (searchTerm, moduloId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.searchHabitaciones(searchTerm, moduloId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al buscar habitaciones';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener estadísticas de una habitación
  const fetchHabitacionStatistics = useCallback(async (habitacionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await habitacionApi.getHabitacionStatistics(habitacionId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener estadísticas de la habitación';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    habitaciones,
    loading,
    error,
    
    // Acciones
    fetchHabitaciones,
    fetchHabitacionesWithStats,
    fetchHabitacionById,
    fetchHabitacionesByModulo,
    createHabitacion,
    updateHabitacion,
    deleteHabitacion,
    searchHabitaciones,
    fetchHabitacionStatistics,
    clearError,
  };
};
